import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { ProjectService } from '../../resources/project/project.service';

/**
 * Api Legacy Core service.
 *
 * This Service is a copy of the last version of the ApiCoreService with legacy API
 * support. Should work as is, and can eventually be removed.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiLegacyCoreService {

  constructor(
    protected httpClient: HttpClient,
    protected projectService: ProjectService
  ) { }

  /**
   * DELETE.
   *
   * @todo Move to ApiCallService.
   *
   * @param params Query params as an array of arrays.
   * @param payload Payload data.
   * @param [options] Headers.
   * @param [includeProject=true] Whether to exclude *project/id/* from route.
   * @returns API response as an observable.
   */
  protected _delete<T>(params: any[][], payload: any, options?: any, includeProject: boolean = true): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.delete<T>(this._generateUrl(params, includeProject), payload).subscribe((response: any) => {
        resolve(response.data);
      });
    });
    return from(call);
  }

   /**
   * GET.
   *
   * @todo Move to ApiCallService.
   *
   * @param params Query params as an array of arrays.
   * @param [includeProject=true] Whether to exclude *project/id/* from route.
   * @returns API response as an observable.
   */
  protected _get<T>(params: any[][], includeProject: boolean = true): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.get<T>(this._generateUrl(params, includeProject)).subscribe((response: any) => {
        resolve(response.data || response);
      });
    });
    return from(call);
  }

  /**
   * PATCH.
   *
   * @todo Create valid patch data object.
   * @todo Move to ApiCallService.
   *
   * @param params Query params as an array of arrays.
   * @param payload Payload data.
   * @param [options] Headers.
   * @param [includeProject=true] Whether to exclude *project/id/* from route.
   * @returns API response as an observable.
   */
  protected _patch<T>(params: any[][], payload: any, options?: any, includeProject: boolean = true): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.patch<T>(this._generateUrl(params, includeProject), payload, options).subscribe((response: any) => {
        resolve(response.data || response);
      });
    });
    return from(call);
  }

  /**
   * POST.
   *
   * @note Some queries do not have a `data` property. In that case, return the whole response.
   *
   * @todo Create valid post data object.
   * @todo Move to ApiCallService.
   *
   * @param params Query params as an array of arrays.
   * @param payload Payload data.
   * @param [options] Headers.
   * @param [includeProject=true] Whether to exclude *project/id/* from route.
   * @returns API response as an observable.
   */
  protected _post<T>(params: any[][], payload: any, options?: any, includeProject: boolean = true): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.post<T>(this._generateUrl(params, includeProject), payload, options).subscribe((response: any) => {
        resolve(response.data || response);
      });
    });
    return from(call);
  }

  /**
   * PUT.
   *
   * @todo Create valid put data object.
   * @todo Move to ApiCallService.
   *
   * @param params Query params as an array of arrays.
   * @param payload Payload data.
   * @param [options] Headers.
   * @param [includeProject=true] Whether to exclude *project/id/* from route.
   * @returns API response as an observable.
   */
  protected _put<T>(params: any[][], payload: any, options?: any, includeProject: boolean = true): Observable<any> {
    const call = new Promise(resolve => {
      this.httpClient.put<T>(this._generateUrl(params, includeProject), payload, options).subscribe((response: any) => {
        resolve(response.data);
      });
    });
    return from(call);
  }

  /**
   * Generate API url from params.
   *
   * @todo Move to ApiCallService.
   *
   * @param routeParams The params to construct the endpoint url with.
   * @param [includeProject] Whether to exclude *project/id/* from route.
   * @returns API url.
   */
  protected _generateUrl(routeParams: string[][], includeProject: boolean): string {
    let url = !includeProject
      ? 'api'
      : `api/project/${ this.projectService.getId() }`;

    routeParams.forEach(controller => {
      url += '/' + controller[0];
      url += controller[1] ? '/' + controller[1] : '';
    });

    return url;
  }
}
